// Login.tsx
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../ui/toast';
import { resendInvite } from '../../utils/api_service/invite_api';
import { getOrganizationLogo } from '../../utils/api_service/organization';
import { apiUrl } from '../../utils/api_service/api_url';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import { TextInput } from '../../ui/input/text_input/text_input';
import { loginUser, requestPasswordChange } from '../../utils/api_service/user_api';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import styles from './login_register.module.css';
import { ProviderIcon } from './ProviderIcon';
import { ResetPassword } from './modal_content/reset_password';
import { validate, validateForm } from './validation/validation_rules';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { setPending } from '../../utils/auth/mfaHandoff';

export const Login: React.FC = () => {
  const history = useHistory();
  const { email } = useParams<{ email: string }>();
  useEffect(() => {
    getOrganizationLogo().then((r) => r.json()).then((d) => {
      if (d && d.auth) setSsoOptions(d.auth.ssoOptions || []);
    }).catch(() => {});
  }, []);
  const { t } = useTranslation(NS.auth);

  const [loginInputs, setLoginInputs] = useState<FormInput>({
    email: email || '',
    password: '',
    errors: { emailError: '', passwordError: '' },
  });
  const [formValid, setFormValid] = useState(true);
  const [ssoOptions, setSsoOptions] = useState<{ provider: string; label: string; kind: string }[]>([]);
  // Deep-link target captured by AuthenticatedRoute (?next=<in-app path>); only
  // in-app paths are honoured, never absolute/protocol-relative URLs.
  const rawNext = new URLSearchParams(window.location.search).get('next') || '';
  const deepLink =
    /^\/(?![/\\])/.test(rawNext) && !rawNext.includes('\\') && rawNext !== '/'
      ? rawNext
      : '';
  const [rememberMe, setRememberMe] = useState(true);
  const [modalState, setModalState] = useState(false);

  const companyLogo = useSelector((state: RootState) => state.logo?.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo?.companyName);

  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    localStorage.removeItem('profilePic');
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const errors = validate(e.target, loginInputs);
    setLoginInputs({
      ...loginInputs,
      [e.target.name]: e.target.value,
      errors,
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm(loginInputs)) {
      toast.error(t(AUTH_KEYS.login.invalidDetails), {
        position: 'bottom-center',
      });
      setFormValid(false);
      return;
    }

    loginUser(loginInputs)
      .then((res) => {
        if (!res.success) {
          if (res.error === 'Not Verified') {
            resendInvite(loginInputs.email);
            toast.info('Your account is not set up yet. We have re-sent your invitation email.',
              { position: 'bottom-center' });
          } else {
            toast.error(res.error, { position: 'bottom-center' });
          }
          return;
        }
        if (rememberMe) {
          localStorage.setItem('email', loginInputs.email);
        }
        // MFA gates: route to the challenge / enrolment step with the token.
        // The deep link is stored with the pending MFA step so the second
        // factor still lands the user where they were headed.
        if (res.mfaRequired && res.challengeToken) {
          setPending('challenge', res.challengeToken, deepLink);
          history.push('/auth/mfa');
          return;
        }
        if (res.mfaEnrollmentRequired && res.enrollToken) {
          setPending('enroll', res.enrollToken, deepLink);
          history.push('/auth/mfa/enroll');
          return;
        }

        finishSignIn(res, { deepLink }).then((target) => history.push(target));
      })
      .catch((err) => toast.error(err, { position: 'bottom-center' }));
  };

  const sendPasswordResetMail = (e: FormEvent, email: string) => {
    e.preventDefault();
    requestPasswordChange(email).then((res) => {
      if (res.success) {
        toast.success(t(AUTH_KEYS.login.resetMailSent), {
          position: 'bottom-center',
        });
        setModalState(false);
      } else {
        toast.error(res.error, { position: 'bottom-center' });
      }
    });
  };

  return (
    <AuthenticationUiLayout>
      <div className='flex flex-col items-center w-full'>
        {/* Top Header Badge or User Avatar Icon (Image 2 style) */}
        {companyName || companyLogo ? (
          <div className='mb-4'>
            <CompanyLogoBadge
              logoUrl={companyLogo}
              companyName={companyName}
              variant='dark-glass'
              altText='Company Logo'
            />
          </div>
        ) : (
          <div className='w-12 h-12 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shadow-inner mb-4'>
            <svg className='w-6 h-6' fill='currentColor' viewBox='0 0 24 24'>
              <path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' />
            </svg>
          </div>
        )}

        <h1 className='text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight text-center'>
          {t(AUTH_KEYS.login.signIn) || 'Welcome back!'}
        </h1>
        <p className='text-slate-500 text-xs sm:text-sm font-normal text-center mt-1 mb-6'>
          {companyName ? `Sign in to access ${companyName}` : 'Enter your login details'}
        </p>

        {/* Login Form */}
        <form
          className='w-full flex flex-col gap-4'
          onSubmit={handleSubmit}
          data-testid='login-form'
        >
          {/* Email Input */}
          <div>
            <TextInput
              type='email'
              label={t(AUTH_KEYS.login.email) || 'Email'}
              name='email'
              variant='pill'
              placeholder='Enter your email'
              value={loginInputs.email}
              onChange={handleChange}
              required
            />
            {!formValid && loginInputs.errors.emailError && (
              <span className={styles.invalidInput}>{loginInputs.errors.emailError}</span>
            )}
          </div>

          {/* Password Input */}
          <div>
            <TextInput
              type='password'
              label={t(AUTH_KEYS.login.password) || 'Password'}
              name='password'
              variant='pill'
              placeholder='Enter your password'
              value={loginInputs.password}
              onChange={handleChange}
              required
            />
            {!formValid && loginInputs.errors.passwordError && (
              <span className={styles.invalidInput}>{loginInputs.errors.passwordError}</span>
            )}
          </div>

          {/* Checkbox and Forgot Password Row */}
          <div className='flex items-center justify-between mt-1 px-1'>
            <label className='flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-700 select-none'>
              <input
                type='checkbox'
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className='w-4 h-4 rounded text-black focus:ring-black border-slate-300 accent-black cursor-pointer'
              />
              <span>Remember me</span>
            </label>

            <span
              onClick={() => setModalState(true)}
              className='text-xs font-semibold text-slate-700 hover:text-black cursor-pointer transition-colors'
            >
              {t(AUTH_KEYS.login.forgotPassword) || 'Forgot password?'}
            </span>
          </div>

          {/* Main CTA Button: Black rounded pill button */}
          <button
            type='submit'
            className='w-full py-3.5 px-6 rounded-full bg-[#050508] hover:bg-slate-800 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.98] mt-3'
          >
            {t(AUTH_KEYS.login.loginButton) || 'Log in'}
          </button>

          {ssoOptions.length > 0 && (
            <>
              <div className='relative flex items-center justify-center my-4'>
                <div className='absolute inset-0 flex items-center'><div className='w-full border-t border-slate-200' /></div>
                <span className='relative px-3 bg-white text-xs font-medium text-slate-400 uppercase tracking-wider'>Or</span>
              </div>
              <div className='flex flex-col gap-2'>
                {ssoOptions.map((opt) => (
                  <button
                      key={opt.provider}
                      type='button'
                      onClick={() => {
                        const returnTo = encodeURIComponent(window.location.origin);
                        const next = encodeURIComponent(deepLink || '/');
                        window.location.href = `${apiUrl}/auth/sso/start?returnTo=${returnTo}&provider=${encodeURIComponent(opt.provider)}&next=${next}`;
                      }}
                      className='w-full py-3 px-6 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-sm transition-all flex items-center justify-center gap-2'
                  >
                    <ProviderIcon provider={opt.provider} />
                    {opt.label || 'Continue with SSO'}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Social sign-in (Google / Microsoft) is rendered here from the
              tenant's configured SSO provider via GET /organization/init once
              SSO ships (Phase 3). Until then no social buttons are shown, so the
              page never advertises a sign-in method that is not available. */}
       
        </form>

        <ResetPassword
          open={modalState}
          onClose={() => setModalState(false)}
          handleSubmit={sendPasswordResetMail}
        />
      </div>
    </AuthenticationUiLayout>
  );
};

